// src/components/CreditsPurchaseModal.jsx
import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector }     from 'react-redux'
import { createOrder, verifyPayment }   from '../store/reducers/creditsSlice'
import { trackVirtualAction }           from '../store/reducers/customerVirtualActivitySlice'

// helper to load the Razorpay checkout script
function loadRazorpayScript() {
  return new Promise(resolve => {
    if (window.Razorpay) return resolve(true)
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.body.appendChild(script)
  })
}

export default function CreditsPurchaseModal({ onClose }) {
  const dispatch       = useDispatch()
  const userId         = useSelector(s => s.auth.userInfo.id)
  const creditsLeft    = useSelector(s => s.credits.remaining)
  const { order, purchasing, purchaseError } = useSelector(s => s.credits)

  // pack options
  const packs = [
    { credits:  50, rupees:  800 },
    { credits: 100, rupees: 1450 },
    { credits: 200, rupees: 2550 },
  ]

  // currently selected pack
  const [selectedPack, setSelectedPack] = useState(null)

  // 1️⃣ load checkout.js once
  useEffect(() => {
    loadRazorpayScript()
  }, [])

  // 2️⃣ handle purchase failures
  useEffect(() => {
    if (purchaseError) {
      dispatch(trackVirtualAction({
        userId,
        actionType: 'credits_purchase_failure',
        metadata:   { error: purchaseError }
      }))
    }
  }, [purchaseError, dispatch, userId])

  // 3️⃣ when we get an order, open Razorpay
  useEffect(() => {
    if (!order || !window.Razorpay) return

    const options = {
      key:         order.key,
      amount:      order.amount,
      currency:    'INR',
      name:        'TrendStreet',
      description: `${order.credits}-credit pack`,
      order_id:    order.orderId,
      handler: res => {
        // verify payment on backend
        dispatch(verifyPayment({
          orderId:           res.razorpay_order_id,
          razorpayPaymentId: res.razorpay_payment_id,
          razorpaySignature: res.razorpay_signature
        }))
        // track success
        dispatch(trackVirtualAction({
          userId,
          actionType: 'credits_purchase_success',
          metadata:   {
            creditsPurchased: selectedPack.credits,
            creditsLeft
          }
        }))
        onClose()
      },
      theme: { color: '#7fad39' }
    }

    new window.Razorpay(options).open()
  }, [order, dispatch, onClose, userId, selectedPack, creditsLeft])

  // kick off order creation
  const handlePay = () => {
    if (!selectedPack) return
    // track start
    dispatch(trackVirtualAction({
      userId,
      actionType: 'credits_purchase_start',
      metadata:   { creditsRequested: selectedPack.credits }
    }))
    dispatch(createOrder({
      credits: selectedPack.credits,
      amount:  selectedPack.rupees * 100
    }))
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-80 p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
        >
          ✕
        </button>
        <h2 className="text-2xl font-bold mb-4">Buy More Credits</h2>

        {/* pack selector */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {packs.map(p => {
            const isSel = selectedPack?.credits === p.credits
            return (
              <div
                key={p.credits}
                onClick={() => setSelectedPack(p)}
                className={`cursor-pointer rounded-lg p-4 flex flex-col items-center border transition-shadow ${
                  isSel
                    ? 'border-green-600 bg-green-50 shadow-lg'
                    : 'border-gray-300 hover:shadow-md'
                }`}
              >
                <div className="text-3xl font-extrabold">{p.credits}</div>
                <div className="text-gray-600 mb-3">Credits</div>
                <div className="text-xl font-semibold">₹{p.rupees}</div>
              </div>
            )
          })}
        </div>

        {/* pay button */}
        <button
          onClick={handlePay}
          disabled={!selectedPack || purchasing}
          className="mt-6 w-full py-2 rounded-lg text-white font-medium bg-green-600 hover:bg-green-700 disabled:opacity-50 transition"
        >
          {purchasing
            ? 'Processing…'
            : selectedPack
              ? `Pay ₹${selectedPack.rupees}`
              : 'Select a Pack'}
        </button>

        {purchaseError && (
          <p className="mt-3 text-center text-red-500 text-sm">
            {purchaseError}
          </p>
        )}
      </div>
    </div>
  )
}
